import { useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { message } from "antd";
import dayjs from "dayjs";
import { getScheduleCheckCallsQuery } from "@src/util/query/site.query";
import { baseURL, liveStatusEndPoint, serverBaseURL } from "@src/config/apollo";
import { downloadBlob, downloadCsv, getLatLngPair } from "./download";

// The per-duty report downloads, shared by Schedules & Activity (report.page.jsx)
// and the duty drawer on Today / Roster.

// templateId -> report template routing, same mapping the job-report download
// already used (wardreport / relyOnVPI / relyOnAlarm / psgAlarm / genericPatrol)
const getReportTemplateName = (templateId) => {
    if (templateId === "67868e4a44e2e036f8eb5969") return "wardreport";
    if (templateId === "66e71ea214797fc1f13c4472") return "relyOnVPI";
    if (templateId === "68915a08d38067ec91f053da") return "relyOnAlarm";
    if (templateId === "6a4ce957508ba6a155d2c191") return "psgAlarm";
    return "genericPatrol";
};

const buildReportUrl = (id, templateId) => {
    const template = getReportTemplateName(templateId);
    if (template === "genericPatrol" || template === "psgAlarm") {
        return baseURL + '/' + template + '/' + id + '/' + templateId;
    }
    return baseURL + '/' + template + '/' + id;
};

const fetchServerPdf = async (path) => {
    const res = await fetch(`${serverBaseURL}${path}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Request failed (${res.status})`);
    }
    return res.blob();
};

export const useDutyReports = () => {
    // ids with a download in flight (report job ids, check call ids, schedule ids)
    const [busyIds, setBusyIds] = useState([]);
    const start = (id) => setBusyIds((prev) => prev.includes(id) ? prev : [...prev, id]);
    const done = (id) => setBusyIds((prev) => prev.filter((existingId) => existingId !== id));
    const isBusy = (id) => busyIds.includes(id);

    const [fetchScheduleCheckCalls] = useLazyQuery(getScheduleCheckCallsQuery, { fetchPolicy: 'network-only' });

    // a submitted report form (mobile job, or a check call's templateId report),
    // rendered through wfc-automation's Puppeteer pipeline
    const downloadReport = async (id, templateId) => {
        if (!id || !templateId) return;
        start(id);
        try {
            const res = await fetch(liveStatusEndPoint + '/mobilereportpdfgenerator', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: buildReportUrl(id, templateId) }),
            });
            downloadBlob(await res.blob(), 'report.pdf');
            message.success("Report downloaded successfully");
        } catch (err) {
            message.error("Failed to download report.");
        } finally {
            done(id);
        }
    };

    // "Task" check calls completed with custom fields rather than a templateId
    // report - wfc-server builds the PDF itself (routes/scheduleTaskReport.route.ts)
    const downloadScheduleTaskReport = async (checkCallId) => {
        if (!checkCallId) return;
        start(checkCallId);
        try {
            downloadBlob(await fetchServerPdf(`/reports/schedule-task/${checkCallId}/pdf`), `task-report-${checkCallId}.pdf`);
            message.success("Report downloaded successfully");
        } catch (err) {
            message.error(err?.message || "Failed to download report.");
        } finally {
            done(checkCallId);
        }
    };

    // guarding duties: every logged callType (clock in/out, check call, task,
    // checkpoint) for that duty, expected vs actual time and location, as a CSV
    const downloadDetailDutyReport = async (scheduleId, siteId) => {
        if (!scheduleId || !siteId) return;
        start(scheduleId);
        try {
            const { data } = await fetchScheduleCheckCalls({ variables: { siteId, scheduleId } });
            const calls = data?.getScheduleCheckCalls?.response || [];

            if (!calls.length) {
                message.info("No activity logged for this duty yet.");
                return;
            }

            // .slice() first - Apollo deep-freezes query results in dev mode
            const rows = calls
                .slice()
                .sort((a, b) => dayjs(a?.requiredAt).valueOf() - dayjs(b?.requiredAt).valueOf())
                .map((call) => {
                    const location = getLatLngPair(call?.actualLocation);
                    return [
                        call?.callType,
                        call?.label,
                        call?.requiredAt ? dayjs(call.requiredAt).format('DD/MM/YYYY HH:mm') : "",
                        call?.actualDate ? dayjs(call.actualDate).format('DD/MM/YYYY HH:mm') : "",
                        call?.pending ? "Pending" : (call?.late ? "Late" : "On time"),
                        location ? location.lat : "",
                        location ? location.lng : "",
                    ];
                });

            downloadCsv(
                `duty-report-${scheduleId}.csv`,
                ["Call Type", "Label", "Expected Date/Time", "Actual Date/Time", "Status", "Lat", "Lng"],
                rows
            );
            message.success("Detail duty report downloaded");
        } catch (err) {
            message.error("Failed to download detail duty report.");
        } finally {
            done(scheduleId);
        }
    };

    // one merged PDF for the whole duty: the activity timeline followed by every
    // check call's submitted report (wfc-server routes/dutyDetailReport.route.ts)
    const downloadDutyDetailPdf = async (scheduleId) => {
        if (!scheduleId) return;
        start(scheduleId);
        try {
            downloadBlob(await fetchServerPdf(`/reports/duty/${scheduleId}/pdf`), `duty-detail-${scheduleId}.pdf`);
            message.success("Duty report downloaded successfully");
        } catch (err) {
            message.error(err?.message || "Failed to download duty report.");
        } finally {
            done(scheduleId);
        }
    };

    return { isBusy, downloadReport, downloadScheduleTaskReport, downloadDetailDutyReport, downloadDutyDetailPdf };
};
